import { useState } from 'react';
import { toSlug } from '../../utils/slug';

/**
 * Chip-style tag input.
 *
 * Tags are normalised through toSlug, so "Spring Boot", "spring boot" and
 * "Spring-Boot" all become `spring-boot`. Without that, the same tag arrives in
 * three spellings and the related-posts matching (which compares tags exactly)
 * silently stops working.
 *
 * @param {string[]} value
 * @param {(tags: string[]) => void} onChange
 */
function TagInput({ value = [], onChange, id = 'tags', max = 8 }) {
  const [draft, setDraft] = useState('');

  const add = (raw) => {
    const tag = toSlug(raw);
    if (!tag) return;
    if (value.includes(tag) || value.length >= max) {
      setDraft('');
      return;
    }
    onChange([...value, tag]);
    setDraft('');
  };

  const removeAt = (index) => onChange(value.filter((_, i) => i !== index));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      removeAt(value.length - 1);
    }
  };

  return (
    <div className="tag-input">
      <ul className="tag-input-list">
        {value.map((tag, i) => (
          <li key={tag} className="chip chip-sm tag-chip">
            #{tag}
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              onClick={() => removeAt(i)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <input
        id={id}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        // Commit whatever is half-typed rather than losing it on blur.
        onBlur={() => add(draft)}
        placeholder={value.length >= max ? `Maximum ${max} tags` : 'Type a tag, press Enter'}
        disabled={value.length >= max}
      />
    </div>
  );
}

export default TagInput;
